import { useMemo, useState } from "react";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useListCarrierAddOnServiceGrants,
  useCreateCarrierAddOnServiceGrant,
  useDeleteCarrierAddOnServiceGrant,
} from "@/api/carrier-add-on-services";
import { useGetCarriers, type Carrier } from "@/api/carriers";
import type {
  CarrierAddOnServiceGrant,
  CarrierAddOnServiceType,
} from "@/api.types.v2";

// Service types are model-generic; only PICKUP is implemented this round, but
// the selector stays so the screen scales when more types ship.
const SERVICE_TYPES: { value: CarrierAddOnServiceType; label: string }[] = [
  { value: "PICKUP", label: "Pickup" },
  { value: "PACKAGING", label: "Packaging" },
  { value: "INSURANCE", label: "Insurance" },
  { value: "SIGNATURE", label: "Signature" },
];

export function CarrierAddOnServiceGrantScreen() {
  const { data: carriersResp, isLoading: carriersLoading } = useGetCarriers();
  const carriers = carriersResp?.data;

  const [providerCarrierId, setProviderCarrierId] = useState<string>("");
  const [serviceType, setServiceType] =
    useState<CarrierAddOnServiceType>("PICKUP");

  // Grants are only fetched once a provider carrier is picked — the query
  // is keyed on both, so switching either picker re-scopes the list.
  const {
    data: grants,
    isLoading: grantsLoading,
    isError: grantsError,
    refetch: refetchGrants,
  } = useListCarrierAddOnServiceGrants(
    providerCarrierId ? { providerCarrierId, serviceType } : undefined
  );

  // labelCarrierId → grant. The grant id is what the delete mutation needs,
  // so the whole row is kept, not just a boolean.
  const grantByLabelCarrier = useMemo(() => {
    const map = new Map<string, CarrierAddOnServiceGrant>();
    for (const g of grants ?? []) map.set(g.labelCarrierId, g);
    return map;
  }, [grants]);

  const providerName =
    carriers?.find((c) => c.id === providerCarrierId)?.name ??
    providerCarrierId;

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-3xl font-bold">Platform Grants</h1>
        <p className="text-gray-600 mt-2 max-w-2xl">
          The platform allowlist (admin-only). Granting a provider carrier a
          service type for a label carrier permits that provider to offer the
          service on that label carrier&apos;s shipments. The carrier still has
          to opt each individual service in — a grant is the ceiling, not the
          switch. Set once per (provider, service type); it applies to all of
          the provider&apos;s services of that type.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pick a provider carrier and service type</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
            <div className="space-y-1">
              <Label htmlFor="grant-provider">Provider carrier</Label>
              {carriersLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <Select
                  value={providerCarrierId}
                  onValueChange={setProviderCarrierId}
                  disabled={!carriers || carriers.length === 0}
                >
                  <SelectTrigger id="grant-provider">
                    <SelectValue placeholder="Select a carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    {(carriers ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="grant-service-type">Service type</Label>
              <Select
                value={serviceType}
                onValueChange={(v) =>
                  setServiceType(v as CarrierAddOnServiceType)
                }
              >
                <SelectTrigger id="grant-service-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!carriersLoading && (!carriers || carriers.length === 0) && (
            <div className="text-center p-8 text-muted-foreground">
              <ShieldCheck className="mx-auto h-12 w-12 mb-2 opacity-50" />
              <p>No carriers exist yet.</p>
              <p className="text-xs mt-1">
                Add carriers before you can grant platform access.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {providerCarrierId && (
        <Card>
          <CardHeader>
            <CardTitle>
              May serve these label carriers
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {providerName} · {serviceTypeLabel(serviceType)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <GrantToggleList
              providerCarrierId={providerCarrierId}
              serviceType={serviceType}
              carriers={carriers ?? []}
              grantByLabelCarrier={grantByLabelCarrier}
              loading={carriersLoading || grantsLoading}
              isError={grantsError}
              onRetry={() => void refetchGrants()}
            />
          </CardContent>
        </Card>
      )}

      {!providerCarrierId &&
        !carriersLoading &&
        carriers &&
        carriers.length > 0 && (
          <div className="text-center p-8 text-muted-foreground">
            <ShieldCheck className="mx-auto h-12 w-12 mb-2 opacity-50" />
            <p>Pick a provider carrier to manage its platform grants.</p>
          </div>
        )}
    </div>
  );
}

function serviceTypeLabel(type: CarrierAddOnServiceType): string {
  return SERVICE_TYPES.find((t) => t.value === type)?.label ?? type;
}

function GrantToggleList({
  providerCarrierId,
  serviceType,
  carriers,
  grantByLabelCarrier,
  loading,
  isError,
  onRetry,
}: {
  providerCarrierId: string;
  serviceType: CarrierAddOnServiceType;
  carriers: Carrier[];
  grantByLabelCarrier: Map<string, CarrierAddOnServiceGrant>;
  loading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const createGrant = useCreateCarrierAddOnServiceGrant();
  const deleteGrant = useDeleteCarrierAddOnServiceGrant();
  const pending = createGrant.isPending || deleteGrant.isPending;

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    // A failed grants fetch must NOT look like "nothing is granted" — that
    // could lead an admin to re-create grants that already exist.
    return (
      <div className="text-center p-8 text-muted-foreground">
        <AlertTriangle className="mx-auto h-10 w-10 mb-2 text-destructive opacity-70" />
        <p>Couldn&apos;t load platform grants.</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }

  if (carriers.length === 0) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        <ShieldCheck className="mx-auto h-10 w-10 mb-2 opacity-50" />
        <p>No carriers to grant.</p>
      </div>
    );
  }

  const handleToggle = (labelCarrierId: string) => {
    const existing = grantByLabelCarrier.get(labelCarrierId);
    if (existing) {
      // Deleting a grant cascade-revokes every dependent enablement on the
      // server — surface the count so the admin knows what they undid.
      deleteGrant.mutate(existing.id, {
        onSuccess: (data) => {
          if (data.cascadedAccessCount > 0) {
            toast.success(
              `Grant revoked. ${data.cascadedAccessCount} carrier enablement${
                data.cascadedAccessCount === 1 ? "" : "s"
              } also revoked.`
            );
          } else {
            toast.success("Grant revoked.");
          }
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to revoke grant.");
        },
      });
    } else {
      createGrant.mutate(
        { providerCarrierId, serviceType, labelCarrierId },
        {
          onSuccess: () => toast.success("Grant created."),
          onError: (err: unknown) => {
            const e = err as {
              response?: { status?: number; data?: { error?: string } };
            };
            toast.error(e.response?.data?.error ?? "Failed to create grant.");
          },
        }
      );
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Label carrier</TableHead>
          <TableHead className="w-[1%] text-right">Granted</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {carriers.map((c) => {
          const grant = grantByLabelCarrier.get(c.id);
          const granted = !!grant;
          return (
            <TableRow key={c.id}>
              <TableCell>
                {c.name}
                {c.id === providerCarrierId && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (self)
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Switch
                  checked={granted}
                  disabled={pending}
                  onCheckedChange={() => handleToggle(c.id)}
                  aria-label={`Grant ${serviceTypeLabel(serviceType)} to ${
                    c.name
                  }`}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
